// pages/admin/ApplicationsPage.tsx
/**
 * @description Admin page to view and manage completed rental applications
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Search,
  ChevronRight,
  X,
  User,
  Mail,
  Phone,
  Calendar,
  Building,
  Home,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Dog,
  Cat,
  Briefcase,
  GraduationCap,
  Globe,
  Filter,
  Loader2,
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { getApplications, updateApplication, getBuildings, type Application } from '../../lib/api';

// Application status types
type ApplicationStatus = 'pending' | 'under_review' | 'approved' | 'rejected';
type ApplicantType = 'professional' | 'student' | 'newcomer' | 'other';
type PetType = 'no' | 'dog' | 'cat' | 'other';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800',
    icon: Clock,
  },
  under_review: {
    label: 'Under Review',
    color: 'bg-blue-100 text-blue-800',
    icon: AlertCircle,
  },
  approved: {
    label: 'Approved',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle,
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-800',
    icon: XCircle,
  },
};

const APPLICANT_TYPE_CONFIG = {
  professional: { label: 'Working Professional', icon: Briefcase },
  student: { label: 'Student', icon: GraduationCap },
  newcomer: { label: 'Newcomer to Canada', icon: Globe },
  other: { label: 'Other', icon: User },
};

const PET_CONFIG = {
  no: { label: 'No pets', icon: null },
  dog: { label: 'Dog', icon: Dog },
  cat: { label: 'Cat', icon: Cat },
  other: { label: 'Other pet', icon: null },
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return formatDate(dateString);
}

export function ApplicationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id');

  const [applications, setApplications] = useState<Application[]>([]);
  const [buildings, setBuildings] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'all'>('all');
  const [buildingFilter, setBuildingFilter] = useState<string>('all');

  // Fetch applications and buildings on mount
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [appsResult, buildingsResult] = await Promise.all([
          getApplications(),
          getBuildings()
        ]);

        if (appsResult.data?.applications) {
          setApplications(appsResult.data.applications);
        }
        if (buildingsResult.data?.buildings) {
          setBuildings(buildingsResult.data.buildings.map(b => ({
            id: b.id,
            name: b.name,
            slug: b.slug
          })));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Get unique buildings from applications
  const buildingsWithApplications = [...new Set(applications.map(a => a.buildingName))];

  // Filter applications
  const filteredApplications = applications.filter((app) => {
    const matchesSearch =
      searchQuery === '' ||
      app.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.buildingName.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesBuilding = buildingFilter === 'all' || app.buildingName === buildingFilter;

    return matchesSearch && matchesStatus && matchesBuilding;
  });

  // Sort by submission date (newest first)
  const sortedApplications = [...filteredApplications].sort(
    (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
  );

  const selectedApplication = selectedId
    ? applications.find((a) => a.id === selectedId)
    : null;

  const closeDetail = () => {
    setSearchParams({});
  };

  const openDetail = (id: string) => {
    setSearchParams({ id });
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Page Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">Inquiries</h1>
          <p className="text-sm lg:text-base text-gray-500 mt-1">
            View and manage inquiries across all properties.
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
          {[
            { label: 'Total', count: applications.length, color: 'text-gray-900' },
            { label: 'Pending', count: applications.filter(a => a.status === 'pending').length, color: 'text-yellow-600' },
            { label: 'Under Review', count: applications.filter(a => a.status === 'under_review').length, color: 'text-blue-600' },
            { label: 'Approved', count: applications.filter(a => a.status === 'approved').length, color: 'text-green-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white p-4 rounded-xl border border-gray-200">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.count}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <div className="p-4 flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400 hidden lg:block" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ApplicationStatus | 'all')}
                className="px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Building Filter */}
            <select
              value={buildingFilter}
              onChange={(e) => setBuildingFilter(e.target.value)}
              className="px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
            >
              <option value="all">All Buildings</option>
              {buildingsWithApplications.map((building) => (
                <option key={building} value={building}>{building}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900">
              Inquiries ({sortedApplications.length})
            </h2>
          </div>

          {sortedApplications.length === 0 ? (
            <div className="p-8 text-center">
              <FileText size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No inquiries found matching your filters.</p>
            </div>
          ) : (
            <>
              {/* Desktop: Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prospect</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedApplications.map((app) => {
                      const StatusIcon = STATUS_CONFIG[app.status].icon;
                      return (
                        <tr
                          key={app.id}
                          onClick={() => openDetail(app.id)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-primary font-medium">
                                  {app.fullName.charAt(0)}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{app.fullName}</p>
                                <p className="text-sm text-gray-500">{app.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-gray-900">{app.buildingName}</p>
                            {app.unitType && (
                              <p className="text-sm text-gray-500">
                                {app.unitType} #{app.unitNumber}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[app.status].color}`}>
                              <StatusIcon size={12} />
                              {STATUS_CONFIG[app.status].label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-gray-600">{getTimeAgo(app.submittedAt)}</p>
                            <p className="text-xs text-gray-400">{formatDate(app.submittedAt)}</p>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <ChevronRight size={18} className="text-gray-400" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile: Cards */}
              <div className="lg:hidden divide-y divide-gray-100">
                {sortedApplications.map((app) => {
                  const StatusIcon = STATUS_CONFIG[app.status].icon;
                  return (
                    <button
                      key={app.id}
                      onClick={() => openDetail(app.id)}
                      className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-medium text-lg">
                          {app.fullName.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-gray-900 truncate">{app.fullName}</p>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[app.status].color}`}>
                            <StatusIcon size={10} />
                            {STATUS_CONFIG[app.status].label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{app.buildingName}</p>
                        {app.unitType && (
                          <p className="text-xs text-gray-500">{app.unitType} #{app.unitNumber}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{getTimeAgo(app.submittedAt)}</p>
                      </div>
                      <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeDetail}
          />

          {/* Modal */}
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Inquiry Details</h2>
                <p className="text-sm text-gray-500 font-mono">{selectedApplication.id}</p>
              </div>
              <button
                onClick={closeDetail}
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-xl mb-6 ${selectedApplication.status === 'approved' ? 'bg-green-50' :
                  selectedApplication.status === 'rejected' ? 'bg-red-50' :
                    selectedApplication.status === 'under_review' ? 'bg-blue-50' :
                      'bg-yellow-50'
                }`}>
                <div className="flex items-center gap-3">
                  {(() => {
                    const StatusIcon = STATUS_CONFIG[selectedApplication.status].icon;
                    return (
                      <StatusIcon size={24} className={
                        selectedApplication.status === 'approved' ? 'text-green-600' :
                          selectedApplication.status === 'rejected' ? 'text-red-600' :
                            selectedApplication.status === 'under_review' ? 'text-blue-600' :
                              'text-yellow-600'
                      } />
                    );
                  })()}
                  <div>
                    <p className="font-medium text-gray-900">
                      Status: {STATUS_CONFIG[selectedApplication.status].label}
                    </p>
                    <p className="text-sm text-gray-600">
                      Submitted {formatDateTime(selectedApplication.submittedAt)}
                    </p>
                  </div>
                </div>
                {selectedApplication.notes && (
                  <p className="mt-3 text-sm text-gray-700 bg-white/50 p-3 rounded-lg">
                    <strong>Notes:</strong> {selectedApplication.notes}
                  </p>
                )}
              </div>

              {/* Applicant Info */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Contact Information
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <User size={18} className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p className="font-medium text-gray-900">{selectedApplication.fullName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{selectedApplication.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{selectedApplication.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const TypeIcon = APPLICANT_TYPE_CONFIG[selectedApplication.applicantType].icon;
                      return <TypeIcon size={18} className="text-gray-400" />;
                    })()}
                    <div>
                      <p className="text-sm text-gray-500">Prospect Type</p>
                      <p className="font-medium text-gray-900">
                        {APPLICANT_TYPE_CONFIG[selectedApplication.applicantType].label}
                      </p>
                    </div>
                  </div>
                  {selectedApplication.hasPets !== 'no' && (
                    <div className="flex items-center gap-3">
                      {PET_CONFIG[selectedApplication.hasPets].icon ? (
                        (() => {
                          const PetIcon = PET_CONFIG[selectedApplication.hasPets].icon!;
                          return <PetIcon size={18} className="text-gray-400" />;
                        })()
                      ) : (
                        <div className="w-[18px]" />
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Pet</p>
                        <p className="font-medium text-gray-900">
                          {PET_CONFIG[selectedApplication.hasPets].label}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Property Info */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Property Interest
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Building size={18} className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Building</p>
                      <p className="font-medium text-gray-900">{selectedApplication.buildingName}</p>
                    </div>
                  </div>
                  {selectedApplication.unitType && (
                    <div className="flex items-center gap-3">
                      <Home size={18} className="text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Unit</p>
                        <p className="font-medium text-gray-900">
                          {selectedApplication.unitType} #{selectedApplication.unitNumber}, Floor {selectedApplication.floor}
                        </p>
                        {selectedApplication.unitPrice && (
                          <p className="text-sm text-gray-500">
                            ${selectedApplication.unitPrice.toLocaleString()}/month
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Preferred Move-in</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(selectedApplication.preferredMoveIn)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 lg:p-6 border-t border-gray-100 flex-shrink-0 bg-white">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    const building = buildings.find(b => b.id === selectedApplication.buildingId);
                    if (building) {
                      navigate(`/admin/buildings/${building.slug}`);
                    }
                  }}
                  className="flex-1 py-3 px-4 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  View Building
                </button>
                <button
                  onClick={() => window.location.href = `mailto:${selectedApplication.email}`}
                  className="flex-1 py-3 px-4 bg-primary text-white font-medium rounded-xl hover:bg-primary-hover transition-colors"
                >
                  Contact Prospect
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
